import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { rulesForAny, type GovernanceRule, type RuleSource, type RuleTag } from "@costadorada/shared";
import { cn } from "@/lib/cn";

// Maps a rule's source document to the DocumentsPage type filter, so "Ver documento"
// deep-links straight to the governing PDF (all these types are owner-visible).
const SOURCE_TO_DOCTYPE: Record<RuleSource, string> = {
  ESCRITURA: "ESCRITURA_MATRIZ",
  REGLAMENTO: "REGLAMENTO",
  MANUAL: "MANUAL_NORMAS",
  EMERGENCIA: "PLAN_EMERGENCIA",
  RACIONAMIENTO: "PLAN_RACIONAMIENTO",
};

/** A single cited rule — reused by RulePanel and the Help Assistant. */
export function RuleCard({ rule, onNavigate }: { rule: GovernanceRule; onNavigate?: () => void }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-sm font-medium">{isEn ? rule.titleEn : rule.titleEs}</div>
      <p className="mt-1 text-sm text-slate-600">{isEn ? rule.summaryEn : rule.summaryEs}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
          {rule.citation}
        </span>
        <Link
          to={`/documents?type=${SOURCE_TO_DOCTYPE[rule.source]}`}
          onClick={onNavigate}
          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {t("rules.viewDocument")} <ExternalLink size={12} />
        </Link>
      </div>
    </div>
  );
}

interface RulePanelProps {
  tags: RuleTag[];
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Collapsible "Applicable rules" panel surfaced inside decision workflows
 * (works permits, incidents, fines, meetings, resolutions, announcements).
 * Renders the cited rules for the given tags so the board/administrator can
 * act correctly and legally protected without re-reading the PDFs.
 */
export function RulePanel({ tags, defaultOpen = false, className }: RulePanelProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const rules = rulesForAny(tags);
  if (rules.length === 0) return null;

  return (
    <div className={cn("rounded-md border border-amber-200 bg-amber-50", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-amber-900">
          <BookOpen size={16} /> {t("rules.applicable")}
          <span className="rounded-full bg-amber-200 px-1.5 text-[11px] text-amber-900">{rules.length}</span>
        </span>
        {open ? <ChevronDown size={16} className="text-amber-700" /> : <ChevronRight size={16} className="text-amber-700" />}
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3">
          {rules.map((rule) => (
            <RuleCard key={rule.id} rule={rule} />
          ))}
        </div>
      )}
    </div>
  );
}
